import { useLocation } from "react-router-dom";
import { COMMISSARY_REPORT_QUERY } from "./fragment";
import { ReportFilters } from "../../components/ReportFilters";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { eventStore } from "../../../../store/event";
import { organizationStore } from "../../../../store/organization";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../../../queries/event";
import { useEffect, useMemo, useRef, useState } from "react";
import { CommissaryOrder } from "../../../../types/order-commissary";
import { useOrganizationStore } from "../../../../store/organization";
import { useEventStore } from "../../../../store/event";
import { groupOrdersByVendor } from "./helpers";
import { CommissaryVendorCard } from "./CommissaryVendorCard";
import CustomSelect from "../../../../components/customSelect";

const client = createApolloClient();
const { getState: getEventState } = eventStore;
const { getState: getOrgState } = organizationStore;

export const CommissaryReport = () => {
  const [orders, setOrders] = useState<CommissaryOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([]);
  const currentOrgId = useOrganizationStore((state) => state.organizationId);
  const currentEventId = useEventStore((state) => state.eventId);

  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  const allVendorGroups = useMemo(() => groupOrdersByVendor(orders), [orders]);
  const vendorSelectOptions = useMemo(
    () =>
      allVendorGroups.map((g) => ({
        label: g.vendorName,
        value: g.vendorId,
      })),
    [allVendorGroups]
  );
  const vendorGroups = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    return allVendorGroups.filter((g) => {
      if (selectedVendorIds.length > 0 && !selectedVendorIds.includes(g.vendorId)) {
        return false;
      }
      if (q && !g.vendorName.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [allVendorGroups, vendorSearch, selectedVendorIds]);

  const toggleOrder = (id: number) =>
    setExpandedOrderId((prev) => (prev === id ? null : id));

  useEffect(() => {
    let isMounted = true;

    const fetchCommissaryOrders = async () => {
      setIsLoading(true);
      try {
        const { eventId } = getEventState();
        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};

        // order_commissary doesn't have organization_id — remove it to avoid query errors
        delete whereVariable.organization_id;

        // Exclude manual payout adjustments — they aren't real commissary orders
        whereVariable.status = { _neq: "adjustment" };

        // Always scope to the selected event, or all events for the org
        if (eventId) {
          whereVariable.event_id = { _eq: eventId };
        } else {
          const organizationId = getOrgState().organizationId;
          if (organizationId) {
            const { data: eventsData } = await client.query({
              query: GET_LIST_EVENTS_BY_ORGANIZATION,
              variables: { where: { organization_id: { _eq: organizationId } } },
              fetchPolicy: "network-only",
            });
            const eventIds = (eventsData?.events || []).map((e: any) => e.id);
            if (eventIds.length > 0) {
              whereVariable.event_id = { _in: eventIds };
            }
          }
        }

        const { data: result } = await client.query({
          query: COMMISSARY_REPORT_QUERY,
          variables: { where: whereVariable },
          fetchPolicy: "network-only",
        });

        if (isMounted) {
          setOrders(result.order_commissary || []);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching commissary report:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchCommissaryOrders, 100);
    };

    debouncedFetch();

    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search, currentOrgId, currentEventId]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 pb-3 mb-4">
        <ReportFilters />
        <div className="flex items-center gap-3 pt-2">
          <input
            type="text"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            placeholder="Search by vendor name..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
          />
          <div className="w-72 flex-shrink-0">
            <CustomSelect
              title="Vendor"
              multi
              closeOnSelect={false}
              value={selectedVendorIds}
              onChange={(selected: any) => {
                const vals = Array.isArray(selected)
                  ? selected.map((o: any) => o.value)
                  : [];
                setSelectedVendorIds(vals);
              }}
              options={vendorSelectOptions}
            />
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && vendorGroups.length === 0 && (
        <div className="text-center py-20 text-gray-500">
          No commissary orders found.
        </div>
      )}

      {/* Vendor cards */}
      {!isLoading && vendorGroups.length > 0 && (
        <div className="space-y-3">
          {vendorGroups.map((group) => (
            <CommissaryVendorCard
              key={group.vendorId}
              group={group}
              expandedOrderId={expandedOrderId}
              onToggleOrder={toggleOrder}
            />
          ))}
        </div>
      )}
    </div>
  );
};
